import { defineMessage } from "react-intl"
import { languages } from "../../i18n"

// Data for public marketing pages, keyed by unlocalized path (without slashes).
// Text fields are react-intl message descriptors.

const publicPages = {
  "student-success": {
    titleMessage: defineMessage({ id: "student-success", defaultMessage: "Student Success" }),
    headingMessage: defineMessage({ id: "student-success-heading", defaultMessage: "When Students Know Their Why <strong>They Stay the Course</strong>" }),
    deckMessage: defineMessage({ id: "student-success-deck", defaultMessage: "Wingspans gives your student success team the tools to help every student find direction, build real skills, and follow through — from their first semester to graduation." }),
  },
  "recruitment": {
    titleMessage: defineMessage({ id: "recruitment", defaultMessage: "Recruitment" }),
    headingMessage: defineMessage({ id: "recruitment-heading", defaultMessage: "<strong>Ignite Possibility</strong> Before They Set Foot on Campus" }),
    deckMessage: defineMessage({ id: "recruitment-deck", defaultMessage: "Real stories bring career paths to life, and institutional matching connects prospective students to the right program before they ever apply." }),
  },
  "community-colleges": {
    titleMessage: defineMessage({ id: "community-colleges", defaultMessage: "Community Colleges" }),
    singularTitleMessage: defineMessage({ id: "community-colleges-singular-title", defaultMessage: "Community college" }),
    headingMessage: defineMessage({ id: "community-colleges-heading", defaultMessage: "The Path to Completion <strong>Starts With a Plan</strong>" }),
    deckMessage: defineMessage({ id: "community-colleges-deck", defaultMessage: "Community colleges like Madison Area Technical College use Wingspans to give every student a clear plan from day one, driving measurable gains in retention and completion." }),
  },
  "four-year-colleges": {
    titleMessage: defineMessage({ id: "four-year-colleges", defaultMessage: "Four-Year Colleges" }),
    singularTitleMessage: defineMessage({ id: "four-year-colleges-singular-title", defaultMessage: "Four-year college" }),
    headingMessage: defineMessage({ id: "four-year-colleges-heading", defaultMessage: "Real Stories Show Students <strong>Where the Degree Leads</strong>" }),
    deckMessage: defineMessage({ id: "four-year-colleges-deck", defaultMessage: "Four-year colleges like College Unbound use Wingspans to help students see where their degree leads, build real skills, and graduate work ready." }),
  },
  "community-based-organizations": {
    titleMessage: defineMessage({ id: "community-based-organizations", defaultMessage: "Community-Based Organizations" }),
    singularTitleMessage: defineMessage({ id: "community-based-organizations-singular-title", defaultMessage: "Community-based organization" }),
    headingMessage: defineMessage({ id: "community-based-organizations-heading", defaultMessage: "Helping Youth and Adults See a Future <strong>and Prepare for It</strong>" }),
    deckMessage: defineMessage({ id: "community-based-organizations-deck", defaultMessage: "Organizations like Foster Forward use Wingspans to help the people they serve — including those impacted by foster care — plan for their future, discover possibilities, and build real work readiness." }),
  },
  "workforce-agencies": {
    titleMessage: defineMessage({ id: "workforce-agencies", defaultMessage: "Workforce Agencies" }),
    singularTitleMessage: defineMessage({ id: "workforce-agencies-singular-title", defaultMessage: "Workforce agency" }),
    headingMessage: defineMessage({ id: "workforce-agencies-heading", defaultMessage: "Building the Workforce <strong>Your Region Needs</strong>" }),
    deckMessage: defineMessage({ id: "workforce-agencies-deck", defaultMessage: "Workforce agencies like Long Beach Workforce Innovation Network use Wingspans to connect youth and adults to local priority sectors — including space — and build the skills to get them work ready." }),
  },
  "employers": {
    titleMessage: defineMessage({ id: "employers", defaultMessage: "Employers" }),
    singularTitleMessage: defineMessage({ id: "employers-singular-title", defaultMessage: "Employer" }),
    headingMessage: defineMessage({ id: "employers-heading", defaultMessage: "<strong>Go Beyond</strong> the Career Fair" }),
    deckMessage: defineMessage({ id: "employers-deck", defaultMessage: "Wingspans gives employers an evergreen way to reach learners throughout their career exploration journey, creating ongoing visibility beyond one-time recruiting events." }),
  },
  "newsletter": {
    titleMessage: defineMessage({ id: "newsletter", defaultMessage: "Newsletter" }),
    headingMessage: defineMessage({ id: "newsletter-heading", defaultMessage: "Subscribe to Our <strong>Newsletter</strong>" }),
  },
  "our-approach": {
    titleMessage: defineMessage({ id: "our-approach", defaultMessage: "Our Approach" }),
    headingMessage: defineMessage({ id: "our-approach-heading", defaultMessage: "Humans Need Humans To <strong>Be Inspired By</strong>" }),
    deckMessage: defineMessage({ id: "our-approach-deck", defaultMessage: "Technology can surface possibilities. People make them feel possible. Wingspans connects students to real stories that expand what they can imagine for their own lives." }),
  },
  "team": {
    titleMessage: defineMessage({ id: "team", defaultMessage: "Team" }),
    headingMessage: defineMessage({ id: "team-heading", defaultMessage: "Creating <strong>Better Paths Forward</strong>" }),
    deckMessage: defineMessage({ id: "team-deck", defaultMessage: "We're a team of people driven by impact, building the kind of career guidance we always wish we'd had — more human, more expansive, and more connected to the possibilities ahead." }),
  }
}

const page = (path) => ({ ...publicPages[path], key: path, path: `/${path}/` })

export const publicPageGroups = [
  {
    key: "platform",
    titleMessage: defineMessage({ id: "public-page-group-platform", defaultMessage: "Platform" }),
    pages: [page("student-success"), page("recruitment")],
    hideInPrivateFooter: true,
  },
  {
    key: "useCases",
    titleMessage: defineMessage({ id: "public-page-group-use-cases", defaultMessage: "Use Cases" }),
    pages: [
      page("community-colleges"),
      page("four-year-colleges"),
      page("community-based-organizations"),
      page("workforce-agencies"),
      page("employers"),
    ],
    hideInPrivateFooter: true,
  },
  {
    key: "about",
    titleMessage: defineMessage({ id: "public-page-about", defaultMessage: "About" }),
    pages: [page("our-approach"), page("team")],
  },
]

// given a route pathname, returns the unlocalized path with leading and
// trailing slashes
export const unlocalizedPublicPath = (path) => {
  const localePrefix = new RegExp(`^/?(${languages.join("|")})(?=/|$)`)
  const key = path.replace(localePrefix, "").replace(/\//g, "")
  return `/${key}/`
}

// given a route pathname, returns the corresponding page data object
export const pathToPublicPageData = (path) => publicPages[unlocalizedPublicPath(path).replace(/\//g, "")]
